import { showErrorToast } from "./toaster";


export const calculateHireVisibilityCharges = (monthlyHireValue, visibilityLevel) => {
  const visibilityData = {
    "1X": { rate: 0.06, min: 15000, max: 90000 },
    "2X": { rate: 0.08, min: 19500, max: 99000 },
    "3X": { rate: 0.12, min: 25350, max: 108900 },
    "4X": { rate: 0.18, min: 32955, max: 119790 },
  };

  const tier = visibilityData[visibilityLevel?.toUpperCase()];
  if (!tier) {
    console.error(`Invalid visibility level: ${visibilityLevel}`);
    return 0;
  }

  const rawCharge = monthlyHireValue * tier.rate;
  const charge = Math.max(tier.min, Math.min(rawCharge, tier.max));

  return charge;
};


export const calculateVisibilityCharges = (
    price,
    maxquantity,
    visibilityLevel,
    totalProducts = 1
  ) => {
    if (!price || !maxquantity || !visibilityLevel) {
      showErrorToast("Price, max quantity, and visibility level are required");
      return 0;
    }
  
    const normalizedVisibilityLevel =
      typeof visibilityLevel === "string" ? visibilityLevel.toUpperCase() : null;
  
    if (!normalizedVisibilityLevel) {
      throw new Error(`Invalid visibility level: ${visibilityLevel}`);
    }
  
    const productValue = price * maxquantity;
  
    let charges;
  
    // For Product Value ≥ ₹500,000
    if (productValue >= 500000) {
      const tierCharges = {
        "1X": 110000,
        "2X": 121000,
        "3X": 133100,
        "4X": 146400,
      };
  
      const baseCharges = tierCharges[normalizedVisibilityLevel];
      const additionalProductCharges =
        totalProducts > 3 ? (totalProducts - 3) * baseCharges : 0;
  
      charges = baseCharges + additionalProductCharges;
    }
    // For Product Value between ₹200,000 and ₹500,000
    else if (productValue >= 200000) {
      const tierCharges = {
        "1X": 95000,
        "2X": 104500,
        "3X": 114950,
        "4X": 126445,
      };
  
      const baseCharges = tierCharges[normalizedVisibilityLevel];
      const additionalProductCharges =
        totalProducts > 5 ? (totalProducts - 5) * baseCharges : 0;
  
      charges = baseCharges + additionalProductCharges;
    }
    // For Product Value < ₹200,000
    else {
      const valueMultipliers = {
        "1X": 4,
        "2X": 6,
        "3X": 8,
        "4X": 11,
      };
  
      const minMaxCharges = {
        "1X": { min: 15000, max: 90000 },
        "2X": { min: 19500, max: 99000 },
        "3X": { min: 25350, max: 108900 },
        "4X": { min: 32955, max: 119790 },
      };
  
      const highestProductValue = productValue; // Use the highest-value product
      const multiplier = valueMultipliers[normalizedVisibilityLevel];
  
      if (!multiplier || !minMaxCharges[normalizedVisibilityLevel]) {
        throw new Error(`Invalid visibility level: ${visibilityLevel}`);
      }
  
      // Calculate visibility charges
      const calculatedCharge = highestProductValue * multiplier;
      const baseCharges = Math.max(
        minMaxCharges[normalizedVisibilityLevel].min,
        Math.min(calculatedCharge, minMaxCharges[normalizedVisibilityLevel].max)
      );
  
      // Adjust charge based on product count
      // s for additional products
      const additionalProductCharges =
        totalProducts > 8 ? (totalProducts - 8) * baseCharges : 0;
  
      charges = baseCharges + additionalProductCharges;
    }
  
    if (typeof charges === "undefined") {
      showErrorToast("Charges calculation failed");
      return 0;
    }
  
    return charges;
  };


  export const calculateCombinedVisibilityCharges = (products, visibilityLevel) => {
    if (!Array.isArray(products) || products.length === 0) {
      showErrorToast("Products are required for visibility charge calculation.");
      console.error("Products array is empty or invalid.");
      return 0;
    }
  
    // Define visibility level tiers
    const tiers = {
      "1X": { min: 15000, max: 90000, multiplier: 4 },
      "2X": { min: 19500, max: 99000, multiplier: 6 },
      "3X": { min: 25350, max: 108900, multiplier: 8 },
      "4X": { min: 32955, max: 119790, multiplier: 11 },
      maxProducts: 8,
    };
  
    const above2LakhAndBelow5LakhTiers = {
      "1X": 95000,
      "2X": 104500,
      "3X": 114950,
      "4X": 126445,
      maxProducts: 5,
    };
  
    const above5LakhTiers = {
      "1X": 110000,
      "2X": 121000,
      "3X": 133100,
      "4X": 146400,
      maxProducts: 3,
    };
  
    const tier = tiers[String(visibilityLevel).toUpperCase()];
    if (!tier) {
      showErrorToast(`Invalid visibility level: ${visibilityLevel}`);
      console.error("Invalid visibility level:", visibilityLevel);
      return 0;
    }
  
    // Find the product with the highest value
    const highestProduct = products.reduce((maxProduct, currentProduct) => {
      const currentValue = (currentProduct.price || 0) * (currentProduct.maxquantity || 1);
      const maxValue = (maxProduct.price || 0) * (maxProduct.maxquantity || 1);
      return currentValue > maxValue ? currentProduct : maxProduct;
    }, products[0]);
  
    const highestProductValue =
      (highestProduct.price || 0) * (highestProduct.maxquantity || 1);
  
    let baseCharge = 0;
    let maxProducts = 0;
  
    // Determine the tier and base charge based on product value
    if (highestProductValue >= 500000) {
      baseCharge = above5LakhTiers[visibilityLevel];
      maxProducts = above5LakhTiers.maxProducts;
    } else if (highestProductValue >= 200000) {
      baseCharge = above2LakhAndBelow5LakhTiers[visibilityLevel];
      maxProducts = above2LakhAndBelow5LakhTiers.maxProducts;
    } else {
      const calculatedCharge = highestProductValue * tier.multiplier;
      baseCharge = Math.max(tier.min, Math.min(calculatedCharge, tier.max));
      maxProducts = 8; // For below ₹2 lakh
    }
  
    // Add extra charges for products beyond the base limit
    //const extraProducts = products.length > maxProducts ? products.length - maxProducts : 0;
   // const extraCharges = extraProducts * baseCharge;
  
    const totalCharges = baseCharge;
  
    // Debug logs for verification
    console.log("Highest Product Value:", highestProductValue);
    console.log("Base Charge:", baseCharge);
   //
    console.log("Total Charges:", totalCharges);
  
    return totalCharges;
  };
  
  



  /**
 * Calculate deposit charges for a product based on its category and subcategory.
 * 
 * @param {string} category - The main category of the product.
 * @param {string} subcategory - The subcategory of the product.
 * @param {number} price - The price of the product.
 * @param {number} quantity - The quantity of the product.
 * @returns {number} - The calculated deposit charges.
 */
export const calculateDepositCharges = (category, subcategory, price, quantity) => {
  if (!category || !subcategory || !price || !quantity) {
    throw new Error("Category, subcategory, price, and quantity are required.");
    return 0;
  }



  const depositPercentages = {
    "Hand Tools": {
      "Manual Hand Tool": 6,
      "Electrical hand tool": 6,
      "Hydrolic Hand Tool": 6,
      "Electrical Hand Tool": 6, // Added missing subcategory

    },
    "Small Equipment": {
      "Concrete mixtures": 5,
      "Concrete Mixing Tools": 5,
      "Surveying and Measurement Tools": 5,
      "Excavation and Digging Tools": 5,
      "Compaction and Tamping Tools": 5,
      "Finishing and Smoothing Tools": 5,
      "Vibrators for Concrete": 5,
    },
    "Structural Material": {
      "Reinforcement steel": 1,
      "Hot Rolled Deformed Bars": 1,
      "Cold Rolled Steel Bars": 1,
      "Mild Steel Bars": 1,
      "Welded Steel Wire.": 1,
      "Prestressing steel bars": 1,
      "Epoxy coated rebar": 1,
      "High yield strength deformed (HYSD) bar": 1,
      "Structural steel Deferent section and size": 1,
      "Alloy steel": 1,
      "Heat-treated carbon steel": 1,
      "Heat-treated alloy steel.": 1 ,
    },
    "Bulk Material/Construction Materials": {
      "Concrete Block": 5,
      "Bricks": 5,
      "Fly Ash ": 5,
      "Bituminous Materials": 4,
      "Sand and Aggregates": 10,
      "Cement and Cement Products": 2,
    },
    "Heavy Equipment": {
      "Concrete Batching Plants": 5,
      "Forklifts and Pallet Jacks": 5,
      "Concrete Pumps": 5,
      "Boom Placers": 5,
      "Porta cabin": 5,
      "Specialized Heavy Equipment's": 5,
      "Power Trencher": 5,
      "Transport Equipment": 5,
      "Plastering and Masonry Equipment": 8.5,
      "Cranes": 8.5,
      "Concrete Equipment": 8.5,
      "Rollers": 5,
      "Excavators and Loaders": 5,
    },
    "Contruction Chemical and Materials": {
      "Concrete Hardeners": 5,
      "Waterproofing Solutions": 5,
      "Surface Treatments": 5,
      "Bonding and Adhesive Agents": 5,
      "Protective Coatings": 5,
      "Grouts and Repair Mortars": 5,
      "Admixtures for Concrete": 5,
      "Insulation Materials": 5,
      "Tiles and Pavers": 5,
      "Joints and Panels": 5,
      "Expansion Joints and Water Stoppers": 5,
      "Paint" : 3 ,
      "Fastener and Anchor" :5,
          "Nails and Screws" :5,
        },

    "Finishing Items": {
      "Rolling Shutters": 5,
      "Windows": 5,
      "Doors": 5,
      "Water Heaters": 5,
      "Sanitary Fixtures": 5,
      "Water Supply Fixtures": 5,
      "Valves": 5,
      "Pipe Fittings": 5,
      "Galvanized Steel Pipes": 5,
      "Copper Pipes": 5,
      "PEX Pipes (Cross-Linked Polyethylene)": 5,
      "CPVC Pipes (Chlorinated Polyvinyl Chloride)": 5,
      "PVC Pipes (Polyvinyl Chloride)": 5,
    },
    "Form Work Material": {
      "Formwork Panels (Modular)": 5,
      "wooden batten": 5,
      "Playwood": 5,
      "Additional Formwork Components": 5,
      "Doka Components": 5,
      "Cup Lock Scaffolding System": 5,
      "Access Scaffolding": 5,
      "HDT System": 5,
      "Wall/Column Formwork": 5,
      "Advanced Scaffolding Systems": 5,
      "Basic Formwork Components": 5,
    },
    "Specialized Equipment's": {
      "Dewatering pumps": 4,
      "D.G.Sets": 4,
      "Specialized Testing Equipment": 4,
      "Specialized Equipment": 4,
      "GPS and Total Stations": 4,
      "Surveying Instruments": 4,
    },
    "Safety Items": {
      "First Aid and Fire Safety Equipment": 5,
      "Fall Protection Equipment": 5,
      "Personal Protective Equipment (PPE)": 5,
    },

    "Electrical Materials" : {
      "Conduits and Fittings": 5,
      "Wires and Cables": 7,
      "Switches and Sockets": 7,
      "Light Fixtures": 7,
      "Distribution Boards and Protection Devices": 10,
      "Earthing and Lightning Protection": 10,
      "Control Panels and Accessories": 5,
      "Communication & Safety": 5,
      "Miscellaneous Items": 10,

    }

  };

  // Get the percentage for the category and subcategory
  const categoryData = depositPercentages[category];
  if (!categoryData) {
    throw new Error(`Invalid category: ${category}`);
  }

  const percentage = categoryData[subcategory];
  if (!percentage) {
    throw new Error(`Invalid subcategory: ${subcategory}`);
  }

  // Calculate the deposit charges
  const totalValue = price * quantity;
  const depositCharges = (totalValue * percentage) / 100;

  return depositCharges;
};


























// import { showErrorToast } from "./toaster";

// export const calculateVisibilityCharges = (
//     price,
//     maxquantity,
//     visibilityLevel,
//     totalProducts = 1
//   ) => {
//     if (!price || !maxquantity || !visibilityLevel) {
//       showErrorToast("Price, max quantity, and visibility level are required");
//       return 0;
//     }
  
//     const normalizedVisibilityLevel =
//       typeof visibilityLevel === "string" ? visibilityLevel.toUpperCase() : null;
  
//     if (!normalizedVisibilityLevel) {
//       throw new Error(`Invalid visibility level: ${visibilityLevel}`);
//     }
  
//     const productValue = price * maxquantity;
  
//     let charges;
  
//     // For Product Value ≥ ₹500,000
//     if (productValue >= 500000) {
//       const tierCharges = {
//         "1X": 110000,
//         "2X": 121000,
//         "3X": 133100,
//         "4X": 146400,
//       };
  
//       const baseCharges = tierCharges[normalizedVisibilityLevel];
//       const additionalProductCharges =
//         totalProducts > 3 ? (totalProducts - 3) * baseCharges : 0;
  
//       charges = baseCharges + additionalProductCharges;
//     }
//     // For Product Value between ₹200,000 and ₹500,000
//     else if (productValue >= 200000) {
//       const tierCharges = {
//         "1X": 95000,
//         "2X": 104500,
//         "3X": 114950,
//         "4X": 126445,
//       };
  
//       const baseCharges = tierCharges[normalizedVisibilityLevel];
//       const additionalProductCharges =
//         totalProducts > 5 ? (totalProducts - 5) * baseCharges : 0;
  
//       charges = baseCharges + additionalProductCharges;
//     }
//     // For Product Value < ₹200,000
//     else {
//       const valueMultipliers = {
//         "1X": 4,
//         "2X": 6,
//         "3X": 8,
//         "4X": 11,
//       };
  
//       const minMaxCharges = {
//         "1X": { min: 15000, max: 90000 },
//         "2X": { min: 19500, max: 99000 },
//         "3X": { min: 25350, max: 108900 },
//         "4X": { min: 32955, max: 119790 },
//       };
  
//       const highestProductValue = productValue; // Use the highest-value product
//       const multiplier = valueMultipliers[normalizedVisibilityLevel];
  
//       if (!multiplier || !minMaxCharges[normalizedVisibilityLevel]) {
//         throw new Error(`Invalid visibility level: ${visibilityLevel}`);
//       }
  
//       // Calculate visibility charges
//       const calculatedCharge = highestProductValue * multiplier;
//       const baseCharges = Math.max(
//         minMaxCharges[normalizedVisibilityLevel].min,
//         Math.min(calculatedCharge, minMaxCharges[normalizedVisibilityLevel].max)
//       );
  
//       // Adjust charge
//       // s for additional products
//       const additionalProductCharges =
//         totalProducts > 8 ? (totalProducts - 8) * baseCharges : 0;
  
//       charges = baseCharges + additionalProductCharges;
//     }
  
//     if (typeof charges === "undefined") {
//       showErrorToast("Charges calculation failed");
//       return 0;
//     }
  
//     return charges;
//   };




//   export const calculateCombinedVisibilityCharges = (products, visibilityLevel) => {
//     if (!Array.isArray(products) || products.length === 0) {
//       showErrorToast("Products are required for visibility charge calculation.");
//       console.error("Products array is empty or invalid.");
//       return 0;
//     }
  
//     // Define visibility level tiers
//     const tiers = {
//       "1X": { min: 15000, max: 90000, multiplier: 4 },
//       "2X": { min: 19500, max: 99000, multiplier: 6 },
//       "3X": { min: 25350, max: 108900, multiplier: 8 },
//       "4X": { min: 32955, max: 119790, multiplier: 11 },
//       maxProducts: 8,
//     };
  
//     const above2LakhAndBelow5LakhTiers = {
//       "1X": 95000,
//       "2X": 104500,
//       "3X": 114950,
//       "4X": 126445,
//       maxProducts: 5,
//     };
  
//     const above5LakhTiers = {
//       "1X": 110000,
//       "2X": 121000,
//       "3X": 133100,
//       "4X": 146400,
//       maxProducts: 3,
//     };
//     console.log("📌 Initial Product Data:", products.map(p => ({
//       id: p._id || "⚠️ Missing ID",
//       name: p.name || "⚠️ Missing Name",
//       price: p.price,
//       maxquantity: p.maxquantity,
//       visibilityLevel: p.visibilityLevel || "⚠️ Missing Visibility"
//   })));
//   console.log("Before Processing Products:", products);
  


//     // Ensure default values for missing properties
//     products = products.map(p => ({
//       ...p,
//       name: typeof p.name === "string" && p.name.trim() !== "" ? p.name : "⚠️ Missing Name",
//       price: p.price || 1,
//       maxquantity: p.maxquantity || 1,
//       visibilityLevel: p.visibilityLevel || "1X",
//   }));
  
//   console.log("✅ After Default Value Assignment:", products.map(p => ({
//     id: p._id || "⚠️ Missing ID",
//     name: p.name,
//     price: p.price,
//     maxquantity: p.maxquantity,
//     visibilityLevel: p.visibilityLevel
// })));

//   // Filter and sort products
//   const sortedProducts = [...products]
//     .filter(p => {
//       if (!p.price || !p.maxquantity) {

//         console.warn(`⚠️ Product ${p.name} has missing attributes. Assigning defaults.`);
//         p.price = p.price || 1;
//         p.maxquantity = p.maxquantity || 1;
//       }
//       return true;
//     })
//     .sort((a, b) => (b.price * b.maxquantity) - (a.price * a.maxquantity));

//   console.log("After Filtering and Sorting:", sortedProducts);

//   let index = 0;
//   let totalCharges = 0;

//   while (index < sortedProducts.length) {
//     console.log(`Processing Group starting at index ${index}`);

//     const highestProduct = sortedProducts[index];
//     if (!highestProduct) {
//       console.warn("⚠️ Invalid product found, skipping.");
//       index++;
//       continue;
//     }
//     console.log("🎯 Highest Product Selected:", {
//       id: highestProduct._id || "⚠️ Missing ID",
//       name: highestProduct.name,
//       price: highestProduct.price,
//       maxquantity: highestProduct.maxquantity,
//       visibilityLevel: highestProduct.visibilityLevel
//   });

//     const highestProductValue = highestProduct.price * highestProduct.maxquantity;
//     console.log("Highest Product:", highestProduct);

//     let selectedTier;
//     let maxProducts = 0;

//     if (highestProductValue >= 500000) {
//       selectedTier = above5LakhTiers;
//       maxProducts = above5LakhTiers.maxProducts;
//     } else if (highestProductValue >= 200000) {
//       selectedTier = above2LakhAndBelow5LakhTiers;
//       maxProducts = above2LakhAndBelow5LakhTiers.maxProducts;
//     } else {
//       selectedTier = tiers;
//       maxProducts = tiers["1X"].maxProducts;
//     }

//     maxProducts = Math.max(1, Math.min(maxProducts, sortedProducts.length - index));

//     // 🔹 Force inclusion if only one product remains
//     if (sortedProducts.length - index === 1) {
//       console.log(`🔹 Only one product remaining. Forcing inclusion: ${sortedProducts[index].name}`);
//       maxProducts = 1;
//     }

//     const selectedProducts = sortedProducts.slice(index, index + maxProducts);
//     if (selectedProducts.length === 0) {
//       console.warn("⚠️ No valid products found in this iteration, adjusting index.");
//       index++;
//       continue;
//     }

//     console.log("Selected Products for this Group:", selectedProducts);

//     const highestVisibility = selectedProducts.reduce((max, p) => {
//       console.log(`🔍 Checking Visibility for: ${p.name} → Original: ${p.visibilityLevel}`);
//       const level = p.visibilityLevel && typeof p.visibilityLevel === "string"
//           ? parseInt(p.visibilityLevel.replace(/[^0-9]/g, ""), 10)  // Extracts numbers safely
//           : 1;
//           console.log(`✅ Parsed Visibility Level for "${p.name}": ${level}X`);
//       return Math.max(max, level);
//   }, 1);
  
//   console.log(`🎯 Final Highest Visibility in Group: ${highestVisibility}X`);
  
//   // Ensure the correct base charge from tiers
//   let baseCharge = 0;
//   if (selectedTier.hasOwnProperty(`${highestVisibility}X`)) {
//     console.log(`✅ Found Matching Tier: ${highestVisibility}X in Selected Tier`);
//       if (highestProductValue < 200000) {
//           const calculatedCharge = highestProductValue * selectedTier[`${highestVisibility}X`].multiplier;
//           baseCharge = Math.max(
//               selectedTier[`${highestVisibility}X`].min,
//               Math.min(calculatedCharge, selectedTier[`${highestVisibility}X`].max)
//           );
//       } else {
//           baseCharge = selectedTier[`${highestVisibility}X`] || 0;
//       }
//   } else {
//       console.warn(`⚠️ No valid tier for visibility ${highestVisibility}X. Defaulting to lowest charge.`);
//       baseCharge = selectedTier["1X"]?.min || 0;
//   }
  
//   console.log(`💰 Corrected Base Charge for Visibility ${highestVisibility}X:`, baseCharge);
  
  

//     totalCharges += baseCharge;
//     console.log("Total Charges so far:", totalCharges);

//     index += selectedProducts.length;
//     console.log("Next Index:", index);
//   }

//   console.log("Final Total Charges Before GST:", totalCharges);
//   return totalCharges;
//   };
  
  
  


//   /**
//  * Calculate deposit charges for a product based on its category and subcategory.
//  * 
//  * @param {string} category - The main category of the product.
//  * @param {string} subcategory - The subcategory of the product.
//  * @param {number} price - The price of the product.
//  * @param {number} quantity - The quantity of the product.
//  * @returns {number} - The calculated deposit charges.
//  */
//   export const calculateDepositCharges = (category, subcategory, price, quantity) => {
//     if (!category || !subcategory || !price || !quantity) {
//       throw new Error("Category, subcategory, price, and quantity are required.");
//     }
  
//     const depositPercentages = {
//       "6720b343ab1e075b95501b26": { // Hand Tools
//         "6721cd78a38df1a2e8e5f03d": 6, // "Manual Hand Tool" : 6,
//         "6721cf4c02475e85b114aa86": 6, // Electrical Hand Tool
//         "6721cfa102475e85b114aa89": 6, // "Hydrolic Hand Tool" : 
//       },
//       "6720b366ab1e075b95501b29": { // Small Equipment
//         "Concrete mixtures": 5,
//         "672f5a40e320b180af7c6476": 5, // Concrete Mixing Tools
//         "672f5a72e320b180af7c6482": 5, // Surveying and Measurement Tools
//         "672f5a85e320b180af7c6485": 5, // Excavation and Digging Tools
//         "672f5a4ee320b180af7c6479": 5, // Compaction and Tamping Tools
//         "672f5a63e320b180af7c647f": 5, // Finishing and Smoothing Tools
//         "672f5a57e320b180af7c647c": 5, // Vibrators for Concrete
//       },
//       "672f5ab0e320b180af7c6488": { // Structural Material
//         "672f5b59e320b180af7c64a3": 1, // Rebar
//         "672f5b69e320b180af7c64a6": 1, // High yield strength deformed (HYSD) bar
//         "672f5b87e320b180af7c64ac": 1, // Structural steel Different section and size
//         "672f5b93e320b180af7c64af": 1, // Alloy steel
//         "672f5b9de320b180af7c64b2": 1, // Heat-treated carbon steel
//         "672f5ba7e320b180af7c64b5": 1, // Heat-treated alloy steel
//       },
//       "6720b3b6ab1e075b95501b32": { // Bulk Material/Construction Materials
//         "672f591ce320b180af7c6445": 5, // Concrete Block
//         "672f590ee320b180af7c6442": 5, // Bricks
//         "672f58d9e320b180af7c6416": 5, // Fly Ash
//         "672f58bce320b180af7c6410": 4, // Bituminous Materials
//         "672f58b0e320b180af7c640d": 10, // Sand and Aggregates
//         "672f5892e320b180af7c640a": 2, // Cement and Cement Products
//       },
//       "6720b38eab1e075b95501b2c": { // Heavy Equipment
//         "672f5800e320b180af7c63da": 4, // Concrete Batching Plants
//         "672f580fe320b180af7c63dd": 4, // Forklifts and Pallet Jacks
//         "672f581ee320b180af7c63e0": 4, // Concrete Pumps
//         "672f582ee320b180af7c63e3": 4, // Boom Placers
//         "672f57ede320b180af7c63d7": 4, // Porta Cabin
//         "672f57e2e320b180af7c63d4": 4, // Specialized Heavy Equipment's
//       },
//       "6720b3a6ab1e075b95501b2f": { // Construction Chemical and Materials
//         "672f5641e320b180af7c6374": 5, // Expansion Joints and Water Stoppers
//         "67a1ab6bcad0094eb68d604e": 3, // Paint
//       },
//       "6720b3d8ab1e075b95501b35": { // Finishing Items
//         "672f55cee320b180af7c6349": 5, // Doors
//         "675bcd5e68f8bf78ed86d0f2": 5, // Windows
//         "672f560ae320b180af7c634f": 5, // Rolling Shutters
//         "672f5508e320b180af7c6328": 5, // PVC Pipes
//         "672f5528e320b180af7c632e": 5, // PEX Pipes
//         "672f5538e320b180af7c6331": 5, // Copper Pipes
//         "672f5545e320b180af7c6334": 5, // Galvanized Steel Pipes
//       },
//       "6720b3f3ab1e075b95501b38": { // Form Work Material
//         "672f541de320b180af7c62e7": 5, // Basic Formwork Components
//         "672f542fe320b180af7c62ea": 5, // Advanced Scaffolding Systems
//         "672f5440e320b180af7c62ed": 5, // Wall/Column Formwork
//         "672f548be320b180af7c62fc": 5, // Additional Formwork Components
//         "672f5499e320b180af7c62ff": 5, // Plywood
//         "672f544ce320b180af7c62f0": 5, // HDT System
//       },
//       "672f50e9e320b180af7c62a6": { // Specialized Equipment
//         "672f53bfe320b180af7c62c3": 4, // D.G. Sets
//         "672f53d8e320b180af7c62c6": 4, // Dewatering Pumps
//         "672f5309e320b180af7c62b7": 4, // Surveying Instruments
//         "672f531ce320b180af7c62ba": 4, // GPS and Total Stations
//         "672f53a4e320b180af7c62c0": 4, // Specialized Testing Equipment
//       },
//       "672f5151e320b180af7c62a9": { // Safety Items
//         "672f52dae320b180af7c62b4": 5, // First Aid and Fire Safety Equipment
//         "672f52bbe320b180af7c62b1": 5, // Fall Protection Equipment
//         "672f52a1e320b180af7c62ae": 5, // Personal Protective Equipment (PPE)
//       },
//     };
    
    
  
//     // Get the percentage for the category and subcategory
//     const categoryData = depositPercentages[category];
//     if (!categoryData) {
//       throw new Error(`Invalid category: ${category}`);
//     }
  
//     const percentage = categoryData[subcategory];
//     if (!percentage) {
//       throw new Error(`Invalid subcategory: ${subcategory}`);
//     }
  
//     // Calculate the deposit charges
//     const totalValue = price * quantity;
//     const depositCharges = (totalValue * percentage) / 100;
  
//     return depositCharges;
//   };




















